import React, { useState, useCallback, useRef } from "react";
import debounce from "lodash.debounce";
import LabledInput from "../../CommonComponents/LabledInput";

// The backend only serves these Places calls to a signed-in admin.
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { authorization: `Bearer ${token}` } : {};
};

// One Places session per search: the keystrokes and the final details call
// share a token, so Google bills them as a single session.
const newSessionToken = () =>
  window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function ApartmentSearch({
  onPlaceSelected,
  showSaveButton = false,
  onSave,
}) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const sessionToken = useRef(newSessionToken());

  // === 1) Call YOUR server-side Autocomplete API ===
  const fetchSuggestions = async (input) => {
    if (!input) return;

    setLoading(true);
    try {
      const res = await fetch(
        `https://api.barbera.in/api/autocomplete?query=${encodeURIComponent(
          input
        )}&sessionToken=${sessionToken.current}`,
        { headers: authHeaders() }
      );
      const data = await res.json();

      if (data.status === "OK") {
        setSuggestions(data.predictions);
      } else {
        console.error("Autocomplete error:", data);
        setSuggestions([]);
      }
    } catch (err) {
      console.error("Autocomplete failed:", err);
      setSuggestions([]);
    }
    setLoading(false);
  };
  // === 2) Debounced version ===
  const debouncedFetch = useCallback(debounce(fetchSuggestions, 300), []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    debouncedFetch(value);
  };

  // === 3) Call YOUR server-side Details API ===
  const handleSelect = async (placeId, description) => {
    try {
      const res = await fetch(
        `https://api.barbera.in/api/place-details?placeId=${encodeURIComponent(
          placeId
        )}&sessionToken=${sessionToken.current}`,
        { headers: authHeaders() }
      );
      sessionToken.current = newSessionToken();
      const data = await res.json();

      if (data.status === "OK") {
        console.log("Full Place Details:", data.result);
        onPlaceSelected(data.result.formatted_address || description || "");

        // update input with selected description or formatted_address
        setQuery(data.result.formatted_address || description || "");
        setSuggestions([]); // clear
      } else {
        console.error("Details API error:", data);
      }
    } catch (err) {
      console.error("Place details fetch failed:", err);
    }
  };

  return (
    <div className="flex flex-col mb-4 relative">
      <div className="flex flex-col sm:flex-row sm:items-end gap-2">
  <div className="flex-1">
    <LabledInput
      value={query}
      onChange={handleInputChange}
      placeholder="Search Apartment..."
      label={"Apartment"}
    />
  </div>

  {showSaveButton && (
   <button
  disabled={!query}
  onClick={() => onSave?.(query)}
  className={`px-4 py-2 rounded sm:h-[42px] font-bold
    ${
      query
        ? "bg-[#FBB034] text-black"
        : "bg-gray-400 cursor-not-allowed"
    }`}
>
  Save
</button>
  )}
</div>

      {loading && (
        <div className="text-gray-300 text-xs absolute mt-1 left-0 z-30">
          Loading...
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="absolute bg-white text-black w-full top-full mt-1 rounded shadow z-50 max-h-60 overflow-y-auto border">
          {suggestions.map((s) => (
            <div
              key={s.place_id}
              className="px-3 py-2 hover:bg-gray-200 cursor-pointer"
              onClick={() => handleSelect(s.place_id, s.description)}
            >
              {s.description}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
